// src/components/atomic-crm/dashboard/DailyBriefing.tsx
import { Sparkles, CalendarClock, TrendingUp, User, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ds/ui/card";
import { useGetList, useTranslate, useLocale } from "ra-core";
import { Link as RouterLink } from "react-router-dom";
import {
  addDays,
  endOfDay,
  endOfToday,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  startOfToday,
  startOfTomorrow,
} from "date-fns";
import { getDateFnsLocale } from "@/i18n/date-fns";

export const DailyBriefing = () => {
  const translate = useTranslate();
  const locale = useLocale();

  const hour = new Date().getHours();
  const greeting =
    hour < 5
      ? "Hello"
      : hour < 12
        ? "Good morning"
        : hour < 17
          ? "Good afternoon"
          : hour < 21
            ? "Good evening"
            : "Hello";

  // Today's open tasks (due today or overdue)
  const { data: tasksRaw } = useGetList("crm_activities", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "due_date", order: "ASC" },
    filter: { type: "task", "done_date@is": null },
  });

  // Upcoming tasks (next 7 days, starting tomorrow)
  const { data: upcomingRaw } = useGetList("crm_activities", {
    pagination: { page: 1, perPage: 3 },
    sort: { field: "due_date", order: "ASC" },
    filter: {
      type: "task",
      "done_date@is": null,
      "due_date@gte": startOfTomorrow().toISOString(),
      "due_date@lte": endOfDay(addDays(new Date(), 7)).toISOString(),
    },
  });

  // Deals closing this week
  const { data: dealsRaw } = useGetList("deals", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "expected_closing_date", order: "ASC" },
    filter: { "archived_at@is": null },
  });

  // Recent contacts
  const { data: contactsRaw } = useGetList("contacts", {
    pagination: { page: 1, perPage: 5 },
    sort: { field: "first_seen", order: "DESC" },
  });

  const tasks = Array.isArray(tasksRaw) ? tasksRaw : (tasksRaw?.data ?? []);
  const upcoming = Array.isArray(upcomingRaw)
    ? upcomingRaw
    : (upcomingRaw?.data ?? []);
  const deals = Array.isArray(dealsRaw) ? dealsRaw : (dealsRaw?.data ?? []);
  const contacts = Array.isArray(contactsRaw)
    ? contactsRaw
    : (contactsRaw?.data ?? []);

  // Date boundaries
  const today = startOfToday();
  const eod = endOfToday();
  const eow = endOfWeek(new Date(), { weekStartsOn: 1 });

  // Declare `parts` before any push
  const parts: string[] = [];

  // Today's and overdue tasks
  const dueToday = tasks
    .filter((t: any) => t.due_date && !isAfter(new Date(t.due_date), eod))
    .slice(0, 5);

  const overdue = dueToday.filter((t: any) => new Date(t.due_date) < today);

  // High priority among today's tasks
  const highPriorityToday = tasks.filter((t: any) => {
    const isHighPriority =
      t.priority?.toLowerCase() === "high" ||
      t.priority?.toLowerCase() === "urgent";
    const isDueToday = t.due_date && !isAfter(new Date(t.due_date), eod);
    return isHighPriority && isDueToday;
  });

  // Deals closing this week
  const closingSoon = deals
    .filter((d: any) => {
      if (!d.expected_closing_date) return false;
      const date = new Date(d.expected_closing_date);
      return !isBefore(date, today) && !isAfter(date, eow);
    })
    .slice(0, 3);

  const latestContact = contacts[0];

  // Fetch the latest contact's company (only if there is one)
  const { data: latestCompanyRaw } = useGetList(
    "companies",
    {
      pagination: { page: 1, perPage: 1 },
      filter: { id: latestContact?.company_id ?? -1 },
    },
    { enabled: !!latestContact?.company_id },
  );

  const latestCompany = Array.isArray(latestCompanyRaw)
    ? latestCompanyRaw[0]
    : latestCompanyRaw?.data?.[0];

  // Build the briefing text (single push per part)
  if (overdue.length > 0) {
    parts.push(
      `${overdue.length} task${overdue.length > 1 ? "s" : ""} overdue`,
    );
  }
  if (dueToday.length > overdue.length) {
    parts.push(`${dueToday.length - overdue.length} due today`);
  }
  if (highPriorityToday.length > 0) {
    parts.push(`${highPriorityToday.length} high priority due today`);
  }
  if (closingSoon.length > 0) {
    parts.push(
      `${closingSoon.length} deal${closingSoon.length > 1 ? "s" : ""} closing this week`,
    );
  }
  const hasOverdue = overdue.length > 0;

  return (
    <Card className="border-none shadow-none bg-muted/30">
      {/* 👈 Removed justify-between. Let the gap do the work. */}
      <CardContent className="p-4 flex flex-col md:flex-row gap-8 items-start md:items-center">
        {/* Left Side: Greeting & Alerts */}
        <div className="flex flex-col gap-2 w-full md:w-auto md:min-w-[250px] shrink-0">
          <div className="flex items-center gap-2 text-indigo-500">
            <Sparkles className="h-4 w-4" />
            <h2 className="text-sm font-semibold uppercase tracking-wide">
              Daily Briefing
            </h2>
          </div>

          {hasOverdue ? (
            <RouterLink
              to="/crm_activities?filter=%7B%22status%22%3A%22overdue%22%7D"
              className="group flex items-center justify-between bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 transition-colors rounded-md px-3 py-1.5 w-fit"
            >
              <span className="text-blue-400 font-medium text-sm">
                {overdue.length} task{overdue.length > 1 ? "s" : ""} overdue
              </span>
              <ArrowRight className="h-3 w-3 text-blue-400 group-hover:translate-x-1 transition-transform ml-3" />
            </RouterLink>
          ) : (
            <p className="text-sm text-muted-foreground">
              <span className="text-foreground font-medium">{greeting}.</span>{" "}
              All clear for today.
            </p>
          )}
        </div>

        {/* Right Side: Contextual Lists */}
        <div className="flex flex-col md:flex-row w-full gap-8">
          {upcoming.length > 0 && (
            <div className="w-full md:w-auto md:min-w-[300px] md:max-w-[400px]">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <CalendarClock className="h-3 w-3" /> Coming up
              </div>
              <ul className="space-y-2">
                {upcoming.slice(0, 2).map((t: any) => (
                  <li key={t.id} className="text-sm">
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="truncate">{t.text}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {format(new Date(t.due_date), "MMM d")}
                      </span>
                    </div>
                    <p className="text-[10px] text-purple-400/80 italic truncate flex items-center gap-1 mt-0.5">
                      <Sparkles className="h-2.5 w-2.5" /> AI: Follow up via
                      phone
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {latestContact && (
            <div className="w-full md:w-auto md:min-w-[150px]">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <User className="h-3 w-3" /> Latest Contact
              </div>
              <div className="text-sm">
                <RouterLink
                  to={`/contacts/${latestContact.id}/show`}
                  className="font-medium hover:underline"
                >
                  {latestContact.first_name} {latestContact.last_name}
                </RouterLink>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};